"use client";

import { c as _c } from "react/compiler-runtime";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { isValidAddressForChain } from "@/hooks/useAddressValidation";
import { atomicToHuman, humanToAtomic } from "@/lib/workflow/tokenAmount";
import type { PropColumn } from "@/stores/templatesStore";
import { type FC } from "react";
import AddressInput from "./AddressInput";

/**
 * Cell registry for CsvTableInput.
 *
 * CsvTableInput renders a grid of user-configurable rows. Each column declares
 * an `inputType` that selects a CellSpec from this registry. A CellSpec
 * encapsulates the three concerns that vary per column type:
 *
 *   - render:   React component that draws the cell editor
 *   - validate: pure function, returns null if valid or an error string
 *   - coerce:   optional normalization applied before storing (e.g. numeric)
 *
 * Adding a new inputType for csvTable columns = adding one entry here. Nothing
 * in CsvTableInput needs to change.
 */

/** Column-level fallback for sibling chain-id lookup. Columns may override via `chainPropKey`. */
export const DEFAULT_CHAIN_PROP_KEY = "chainId";

/**
 * Token metadata carried alongside a tokenAmount column. Feeds the cell's
 * helper text and the sum-aggregate formatter. Only populated once the
 * ambient `useSimTokensInfo` call in `ConnectorInputModal` resolves — cells
 * degrade gracefully (no helper, no sum formatting) until then.
 */
export interface TokenInfo {
  decimals: number;
  symbol?: string;
  name?: string;
}
export interface CellContext {
  /**
   * Resolve the chainId a cell should validate against. Prefers the column's
   * `chainPropKey` (explicit sibling reference), falls back to a top-level
   * `chainId` prop. Returns `undefined` when no chain prop is wired — cells
   * then fall back to chain-agnostic address validation.
   */
  chainIdFor?: (column: PropColumn) => number | undefined;
  /**
   * Resolve token metadata for a column whose `tokenAddressPropKey`/`chainPropKey`
   * point at a selected ERC-20. Returns `undefined` while the lookup is pending
   * (or when no token props are wired) so cells can degrade gracefully.
   */
  tokenInfoFor?: (column: PropColumn) => TokenInfo | undefined;
}
function coerceChainId(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.length > 0) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/**
 * Build a CellContext for a given propValues record. Used by both
 * CsvTableInput (at render time) and ConnectorInputModal (at validate time)
 * so the two stay in sync on how chain IDs are resolved.
 *
 * `tokenInfoByColKey` is optional — the modal's validate-time call supplies
 * it so validators that want to enforce fractional-digit bounds (tokenAmount)
 * can reach decimals, and so the summary footer can format sums with symbol.
 */
export function buildCellContext(propValues: Record<string, unknown> | undefined, tokenInfoByColKey?: Record<string, TokenInfo | undefined>): CellContext {
  return {
    // Resolve in priority order so both declaration shapes work:
    //   1. column.chainPropKey  → look up the sibling prop's runtime value
    //   2. column.chainId       → literal hardcoded on the column schema
    //   3. propValues[DEFAULT_CHAIN_PROP_KEY] → legacy fallback for templates
    //      that wire chain via a top-level "chainId" prop without declaring
    //      `chainPropKey` on the column.
    chainIdFor: column => coerceChainId(column.chainPropKey ? propValues?.[column.chainPropKey] : column.chainId ?? propValues?.[DEFAULT_CHAIN_PROP_KEY]),
    tokenInfoFor: tokenInfoByColKey ? column => tokenInfoByColKey[column.key] : undefined
  };
}
export interface CellRenderProps {
  column: PropColumn;
  value: string;
  onChange: (raw: string) => void;
  ctx: CellContext;
  invalid: boolean;
}

/**
 * Output of a column-level aggregation (e.g. sum of a tokenAmount column,
 * count of a recipient list). `raw` is machine-friendly; `formatted` is the
 * label surfaced in the summary footer.
 */
export interface AggregateResult {
  raw: string | number;
  formatted: string;
}
export interface CellSpec {
  render: FC<CellRenderProps>;
  /** Return null when the value is valid, or a short human-readable reason when it's not. Empty string is always treated as valid here; required-field checks live at the grid level. */
  validate?: (raw: string, column: PropColumn, ctx: CellContext) => string | null;
  /** Normalize the stored value. Runs before the value reaches the column-wise storage. */
  coerce?: (raw: string, column: PropColumn) => string | number;
  /**
   * Cell-type aggregation hooks. `values` is pre-filtered to non-empty cells
   * from rows that pass validation — aggregates never account for invalid
   * input. `undefined` result means the aggregate is unavailable for this
   * column right now (e.g. token metadata not yet loaded).
   */
  aggregate?: {
    sum?: (values: string[], column: PropColumn, ctx: CellContext) => AggregateResult | undefined;
    count?: (values: string[], column: PropColumn, ctx: CellContext) => AggregateResult | undefined;
  };
}

/**
 * Plain decimal (optionally leading with "."), no sign, no scientific
 * notation, no separators. Matches the shape `humanToAtomic` accepts.
 */
const DECIMAL_RE = /^(\d+(\.\d+)?|\.\d+)$/;
function inputModeFor(column: PropColumn): "numeric" | "decimal" | undefined {
  if (column.inputType === "tokenAmount") return "decimal";
  if (column.type === "integer") return "numeric";
  if (column.type === "number") return "decimal";
  return undefined;
}

/** Default text cell — used for any column whose `inputType` isn't in the registry, and as the numeric fallthrough. */
const DefaultCell: FC<CellRenderProps> = t0 => {
  const $ = _c(12);
  const {
    column,
    value,
    onChange,
    invalid
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = column.placeholder ?? column.name;
  const t3 = invalid && "border-red-500 focus-visible:ring-red-500";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("h-9 text-sm text-zinc-900 dark:text-white", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== column) {
    t5 = inputModeFor(column);
    $[4] = column;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t4 || $[9] !== t5 || $[10] !== value) {
    t6 = <Input type="text" value={value} onChange={t1} placeholder={t2} className={t4} inputMode={t5} />;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
const AddressCell: FC<CellRenderProps> = t0 => {
  const $ = _c(16);
  const {
    column,
    value,
    onChange,
    ctx,
    invalid
  } = t0;
  const t1 = invalid && "rounded-md ring-1 ring-red-500";
  let t2;
  if ($[0] !== t1) {
    t2 = cn(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onChange) {
    t3 = v => onChange(v || "");
    $[2] = onChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = column.placeholder ?? "0x...";
  let t5;
  if ($[4] !== column || $[5] !== ctx) {
    t5 = ctx.chainIdFor?.(column);
    $[4] = column;
    $[5] = ctx;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== column.required || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== value) {
    t6 = <AddressInput label="" value={value} onChange={t3} placeholder={t4} required={column.required} chainId={t5} popoverModal={true} compact={true} />;
    $[7] = column.required;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t6) {
    t7 = <div className={t2}>{t6}</div>;
    $[13] = t2;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};

/**
 * Human-only tokenAmount cell.
 *
 * Storage = the raw human string the user types (e.g. "1.5"). No local state,
 * no async reformatting — what the user types is what's stored. When token
 * metadata is available, a small helper line renders below the input showing
 * the computed atomic representation. Metadata arriving async purely *adds*
 * the helper — it never changes the user's input, so there's no flicker.
 *
 * Conversion to atomic units happens once at submit time in
 * `ConnectorInputModal`, not per-keystroke here.
 */
const TokenAmountCell: FC<CellRenderProps> = t0 => {
  const $ = _c(14);
  const {
    column,
    value,
    onChange,
    ctx,
    invalid
  } = t0;
  const info = ctx.tokenInfoFor?.(column);
  const placeholder = column.placeholder ?? (info?.symbol ? `1.5 ${info.symbol}` : "1.5");
  let helper = null;
  if (value && info) {
    const atomic = humanToAtomic(value, info.decimals);
    if (atomic !== null) {
      helper = `= ${atomic} atomic`;
    }
  }
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = invalid && "border-red-500 focus-visible:ring-red-500";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("h-9 text-sm text-zinc-900 dark:text-white", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== placeholder || $[5] !== t1 || $[6] !== t3 || $[7] !== value) {
    t4 = <Input type="text" value={value} onChange={t1} placeholder={placeholder} inputMode="decimal" className={t3} />;
    $[4] = placeholder;
    $[5] = t1;
    $[6] = t3;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== helper) {
    t5 = helper ? <p className="mt-1 text-xs text-zinc-500">{helper}</p> : null;
    $[9] = helper;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};

/**
 * Validate a human-units tokenAmount string.
 *
 * - Empty is valid here (required-field checks live at the grid level).
 * - Shape: plain decimal, no sign, no separators, no scientific notation.
 * - Value: must be > 0 (disperse/transfer actions reject zero transfers).
 * - When token metadata is loaded, additionally enforces fractional-digit
 *   count <= decimals so over-precision is caught at the cell instead of
 *   silently dropped by humanToAtomic at submit time.
 */
const tokenAmountValidate: CellSpec["validate"] = (raw, col, ctx) => {
  if (!raw) return null;

  // Specific hints before the generic shape check.
  if (/\s/.test(raw)) return "Remove whitespace.";
  if (raw.startsWith("-")) return "Amount must be greater than 0.";
  if (/e/i.test(raw)) return "Use the full decimal (e.g. 1.5), not scientific notation.";
  if (!DECIMAL_RE.test(raw)) return "Enter a decimal number (e.g. 1.5) in human units.";

  // Reject zero / 0.0 / 0.000... — all represent no transfer.
  if (!/[1-9]/.test(raw)) return "Amount must be greater than 0.";
  const info = ctx.tokenInfoFor?.(col);
  if (info) {
    const dotIdx = raw.indexOf(".");
    const fracLen = dotIdx === -1 ? 0 : raw.length - dotIdx - 1;
    if (fracLen > info.decimals) {
      return info.decimals === 0 ? "This token is indivisible — use a whole number." : `Max ${info.decimals} decimals for this token.`;
    }
  }
  return null;
};
const addressValidate: CellSpec["validate"] = (raw, col, ctx) => {
  if (!raw) return null;
  const chainId = ctx.chainIdFor?.(col);
  if (isValidAddressForChain(raw, chainId)) return null;
  return chainId ? "Invalid blockchain address for this chain." : "Invalid blockchain address.";
};

/** Numeric coercer: stores typed numbers for number/integer columns so
 * JSONB keeps the right type through the workflow POST.
 *
 * Caveat for csvTable: JS Number caps integer precision at 2^53. Columns
 * holding atomic token amounts or any large big-int must use
 * `inputType: "tokenAmount"` (string storage) — Go's `json.Unmarshal`
 * round-trips these as float64 and silently loses precision otherwise. */
const numericCoerce: CellSpec["coerce"] = (raw, col) => {
  if (raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  if (col.type === "integer" && !Number.isInteger(n)) return raw;
  return n;
};
const numericValidate: CellSpec["validate"] = (raw, col) => {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return "Must be a number.";
  if (col.type === "integer" && !Number.isInteger(n)) return "Must be an integer.";
  return null;
};

/** Universal count aggregate — `values` is already pre-filtered to valid non-empty cells. */
const countAggregate: NonNullable<CellSpec["aggregate"]>["count"] = values => ({
  raw: values.length,
  formatted: String(values.length)
});

/**
 * Sum human-units tokenAmount values as BigInt atomic, then format back to
 * human with token decimals + symbol. Each value is scaled through
 * `humanToAtomic` using the column's token metadata.
 *
 * Without metadata there's no way to interpret fractional inputs meaningfully
 * — returns the neutral "0 atomic" label so the summary footer has something
 * to display until decimals arrive.
 *
 * `undefined` means the aggregate is unavailable (at least one value failed to
 * parse under current decimals — the caller already pre-filters invalid cells,
 * so this path should only hit defensively).
 */
const tokenAmountSum: NonNullable<CellSpec["aggregate"]>["sum"] = (values, col, ctx) => {
  const info = ctx.tokenInfoFor?.(col);
  if (!info) {
    // No decimals → can't scale fractional inputs. Give a neutral placeholder.
    return {
      raw: "0",
      formatted: "0 atomic"
    };
  }
  let total = 0n;
  for (const v of values) {
    const atomic = humanToAtomic(v, info.decimals);
    if (atomic === null) return undefined;
    total += BigInt(atomic);
  }
  const rawStr = total.toString();
  const human = atomicToHuman(rawStr, info.decimals);
  return {
    raw: rawStr,
    formatted: info.symbol ? `${human} ${info.symbol}` : human
  };
};
export const CELL_REGISTRY = {
  address: {
    render: AddressCell,
    validate: addressValidate,
    aggregate: {
      count: countAggregate
    }
  },
  recipientAddress: {
    render: AddressCell,
    validate: addressValidate,
    aggregate: {
      count: countAggregate
    }
  },
  tokenAmount: {
    render: TokenAmountCell,
    validate: tokenAmountValidate,
    aggregate: {
      sum: tokenAmountSum,
      count: countAggregate
    }
  },
  text: {
    render: DefaultCell,
    aggregate: {
      count: countAggregate
    }
  }
} as const satisfies Record<string, CellSpec>;

/** Source of truth for column inputTypes — keeps the TS union of `PropColumn.inputType` in lockstep with the registry. */
export type CellInputType = keyof typeof CELL_REGISTRY;

/**
 * Resolve a CellSpec for a column. Falls back to the default text cell and,
 * when the column's `type` is numeric, layers in the numeric validator and
 * coercer so even unknown inputTypes benefit from number handling.
 */
export function getCellSpec(column: PropColumn): CellSpec {
  const direct = column.inputType ? CELL_REGISTRY[column.inputType] : undefined;
  if (direct) return direct;
  if (column.type === "number" || column.type === "integer") {
    return {
      render: DefaultCell,
      validate: numericValidate,
      coerce: numericCoerce
    };
  }
  return CELL_REGISTRY.text;
}

/** Cell error key — `${rowIndex}:${columnKey}`. */
export type CellErrorMap = Record<string, string>;
export interface CsvTableValidationOpts {
  /** Whether the csvTable prop itself is marked required. Drives "no rows" semantics. */
  required?: boolean;
  /** Minimum rows the template declares. Empty tables below this block submit. */
  minRows?: number;
}
export interface CsvTableValidation {
  /** Per-cell error messages keyed by "rowIndex:columnKey". Empty if valid. */
  cellErrors: CellErrorMap;
  /** True when the value is submit-ready given the options. */
  isValid: boolean;
  /** Number of rows observed in the value (columnar max length). */
  rowCount: number;
  /** Number of rows that are entirely empty across all columns. */
  emptyRowCount: number;
}
function cellToString(v: unknown): string {
  return v == null ? "" : String(v);
}

/**
 * Single-pass validation of a csvTable prop value. Drives both the in-grid
 * error display (cellErrors) and the submit gate (isValid) so the two can
 * never disagree.
 *
 * Semantics:
 * - Each non-empty cell runs its registered format validator; failures
 *   populate cellErrors.
 * - Empty cells in required columns add a "Required" cellError IF the
 *   row is not entirely empty. Entirely-empty rows are tolerated (they
 *   strip out at submit time) so users can click Add row without being
 *   punished.
 * - isValid requires: every required column has at least one row with a
 *   value OR the prop is not required; every populated cell passes its
 *   format validator; every required cell in a non-empty row is filled.
 * - When `required` is true and the table has only empty rows (or no
 *   rows), isValid is false.
 * - When `required` is false and the table is empty, isValid is true.
 */
export function validateCsvTable(value: unknown, columns: PropColumn[], ctx: CellContext = {}, opts: CsvTableValidationOpts = {}): CsvTableValidation {
  const cellErrors: CellErrorMap = {};
  if (!columns.length) return {
    cellErrors,
    isValid: true,
    rowCount: 0,
    emptyRowCount: 0
  };
  const isObj = value != null && typeof value === "object";
  const v = isObj ? value as Record<string, unknown> : {};
  const rowCount = Math.max(0, ...columns.map(c => Array.isArray(v[c.key]) ? (v[c.key] as unknown[]).length : 0));
  let emptyRowCount = 0;
  let anyRowPopulated = false;
  let formatFailure = false;
  let missingRequired = false;
  for (let i = 0; i < rowCount; i++) {
    const cells = columns.map(c => cellToString(Array.isArray(v[c.key]) ? (v[c.key] as unknown[])[i] : undefined));
    const rowEmpty = cells.every(c => c === "");
    if (rowEmpty) {
      emptyRowCount++;
      continue;
    }
    anyRowPopulated = true;
    columns.forEach((col, ci) => {
      const raw = cells[ci];
      if (!raw) {
        if (col.required) {
          cellErrors[`${i}:${col.key}`] = "Required";
          missingRequired = true;
        }
        return;
      }
      const spec = getCellSpec(col);
      const err = spec.validate?.(raw, col, ctx) ?? null;
      if (err) {
        cellErrors[`${i}:${col.key}`] = err;
        formatFailure = true;
      }
    });
  }
  const effectiveMin = Math.max(0, opts.minRows ?? 0);
  const populatedRowCount = rowCount - emptyRowCount;
  let isValid = !formatFailure && !missingRequired;
  // minRows is gated on `required` by design: an optional csvTable is treated
  // as all-or-nothing — a user can leave it entirely empty, but if they start
  // filling rows we still reject bad formats (above). We deliberately do NOT
  // enforce minRows on optional tables, because partial input shouldn't lock
  // them out of submitting a form where the table was never mandatory.
  if (opts.required) {
    if (!anyRowPopulated) isValid = false;
    if (populatedRowCount < effectiveMin) isValid = false;
  }
  return {
    cellErrors,
    isValid,
    rowCount,
    emptyRowCount
  };
}

/**
 * Thin wrapper preserved for callers that only care about the boolean gate.
 * Prefer `validateCsvTable` when you need the cell error map too.
 */
export function isCsvTableValueValid(value: unknown, columns: PropColumn[], ctx: CellContext = {}, opts: CsvTableValidationOpts = {}): boolean {
  return validateCsvTable(value, columns, ctx, opts).isValid;
}

/**
 * Drop rows that are entirely empty across all columns. Keeps the stored
 * value tight before submission and prevents empty-cell sentinels from
 * reaching downstream actions as `""` in parallel arrays.
 */
export function stripEmptyRows(value: unknown, columns: PropColumn[]): Record<string, Array<string | number>> | unknown {
  if (!columns.length || value == null || typeof value !== "object") return value;
  const v = value as Record<string, unknown>;
  const rowCount = Math.max(0, ...columns.map(c => Array.isArray(v[c.key]) ? (v[c.key] as unknown[]).length : 0));
  if (rowCount === 0) return value;
  const keepIndices: number[] = [];
  for (let i = 0; i < rowCount; i++) {
    const rowEmpty = columns.every(c => cellToString(Array.isArray(v[c.key]) ? (v[c.key] as unknown[])[i] : undefined) === "");
    if (!rowEmpty) keepIndices.push(i);
  }
  if (keepIndices.length === rowCount) return value;
  const out: Record<string, Array<string | number>> = {};
  for (const col of columns) {
    const arr = Array.isArray(v[col.key]) ? v[col.key] as Array<string | number> : [];
    out[col.key] = keepIndices.map(i => (arr[i] ?? "") as string | number);
  }
  return out;
}